import React, { useMemo } from 'react';
import { Placeholder } from 'rsuite';
import { useAuth } from '../../contexts/AuthContext';
import { useProfilePhoto } from '../../hooks/useQueries';

const sizePxByToken = {
  small: 40,
  medium: 72,
  large: 120,
};

export default function ProfilePhoto({ employeeId, size = 'large', fallbackInitials, firstName, lastName }) {
  const auth = useAuth();
  const resolvedEmployeeId = employeeId || 'me';
  const isMe = resolvedEmployeeId === 'me';

  const photoQuery = useProfilePhoto(resolvedEmployeeId, { enabled: true });

  const px = sizePxByToken[size] || sizePxByToken.large;

  const initials = useMemo(() => {
    const provided = String(fallbackInitials || '').trim();
    if (provided) {
      const cleaned = provided.replace(/\s+/g, '').slice(0, 2).toUpperCase();
      return cleaned || 'U';
    }

    if (!isMe) {
      const a = String(firstName || '').trim().slice(0, 1);
      const b = String(lastName || '').trim().slice(0, 1);
      const text = `${a}${b}`.toUpperCase();
      return text || 'U';
    }

    const first = auth.employee?.first_name || '';
    const last = auth.employee?.last_name || '';
    const a = first.trim().slice(0, 1);
    const b = last.trim().slice(0, 1);
    const text = `${a}${b}`.toUpperCase();
    return text || 'U';
  }, [auth.employee?.first_name, auth.employee?.last_name, fallbackInitials, firstName, isMe, lastName]);

  if (photoQuery.isLoading) {
    return (
      <div style={{ width: px, height: px, borderRadius: '50%', overflow: 'hidden' }}>
        <Placeholder.Graph active height={px} width={px} />
      </div>
    );
  }

  const photo = photoQuery.data;
  const src = photo?.base64 && photo?.contentType ? `data:${photo.contentType};base64,${photo.base64}` : null;

  if (src) {
    return (
      <img
        src={src}
        alt="Profile"
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid var(--dm-border, rgba(0,0,0,0.1))'
        }}
      />
    );
  }

  return (
    <div
      aria-label="Profile photo"
      style={{
        width: px,
        height: px,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6c757d, #adb5bd)',
        color: 'white',
        fontWeight: 700,
        fontSize: Math.max(14, Math.floor(px / 3)),
        border: '1px solid var(--dm-border, rgba(0,0,0,0.1))'
      }}
    >
      {initials}
    </div>
  );
}
